// src/chatbot/components/CartButton.tsx
import { motion } from "framer-motion";
import React, { useState, useEffect } from "react";
import { BiShoppingBag } from "react-icons/bi";
import { useBranch } from "~/contexts/BranchContext";
import { useShoppingCart } from "~/contexts/ShoppingCartContext";
import { useTranslation } from "react-i18next";
import CartModal from "../CartModal";
import type { MenuItem } from "~/types/new_types";

interface CartButtonProps {
  onItemClick?: (item: MenuItem) => void;
  position?: string;
}

const CartButton: React.FC<CartButtonProps> = ({ onItemClick, position }) => {
  const { branch, theme, selectedItem } = useBranch();
  const { tempQuantities, isCartDisabled, isCartOpen, openCart, closeCart } =
    useShoppingCart();
  const { t, i18n } = useTranslation();
  const [isItemModalOpening, setIsItemModalOpening] = useState(false);

  // Track when selectedItem changes to manage modal transitions
  // IMPORTANT: This hook must be called unconditionally before any early returns
  useEffect(() => {
    if (selectedItem && isCartOpen) {
      setIsItemModalOpening(true);
    } else if (!selectedItem && isItemModalOpening) {
      setIsItemModalOpening(false);
      // Cart modal should remain open when returning from item details
    }
  }, [selectedItem, isCartOpen, isItemModalOpening]);

  // Calculate number of unique items in cart (not total quantity)
  const totalCartItems = Object.values(tempQuantities).filter(
    (item) => item.quantity > 0
  ).length;

  // Don't render cart button if cart is disabled
  if (isCartDisabled) {
    return null;
  }

  // Only show cart button when there are items in cart
  if (totalCartItems === 0) return null;

  // Don't close cart modal when MenuItemDetailsModal opens or when transitioning
  const handleCartModalClose = () => {
    // Only close if no item details modal is open and not transitioning
    if (!selectedItem && !isItemModalOpening) {
      closeCart();
    }
  };

  // Wrapper for onItemClick to track the transition
  const handleItemClick = (item: MenuItem) => {
    if (onItemClick) {
      setIsItemModalOpening(true);
      onItemClick(item);
    }
  };

  const openCartModal = () => {
    openCart();
  };

  // Determine direction for button placement
  const isRTL = i18n.language === "ar";

  // Style based on position
  const getButtonStyles = () => {
    if (position === "header") {
      return {
        className:
          "w-6 h-6 rounded-full flex items-center justify-center text-white hover:bg-white/20 cursor-pointer relative",
        style: {
          backgroundColor: "rgba(255,255,255,0.2)",
        },
      };
    }

    // Default fixed position style
    return {
      className: `${position} ${isRTL ? "left-8" : "right-8"
        } z-30 w-10 h-10 rounded-full flex items-center justify-center cursor-pointer`,
      style: {
        backgroundColor: theme?.primaryColor,
        color: "#ffffff",
        boxShadow: `0 8px 25px ${theme?.primaryColor}40`,
      },
    };
  };

  const buttonStyles = getButtonStyles();

  return (
    <>
      <motion.button
        onClick={openCartModal}
        className={buttonStyles.className}
        style={buttonStyles.style}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 500, damping: 20 }}
        title="View Cart"
      >
        <BiShoppingBag size={position === "header" ? 14 : 20} />

        {/* Cart count badge */}
        {totalCartItems > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 15,
            }}
            style={{
              backgroundColor:
                position === "header" ? "white" : theme?.secondaryColor,
              color: position === "header" ? theme?.primaryColor : "white",
            }}
            className={`absolute ${position === "header"
              ? "-top-1 -right-1 text-xs"
              : "-top-2 -right-2 text-xs"
              } font-bold rounded-full ${position === "header"
                ? "min-w-[16px] h-[16px]"
                : "min-w-[20px] h-[20px]"
              } flex items-center justify-center px-1 shadow-lg`}
          >
            {totalCartItems > 99 ? "99+" : totalCartItems}
          </motion.div>
        )}
      </motion.button>

      <CartModal
        isOpen={isCartOpen}
        onClose={handleCartModalClose}
        onItemClick={handleItemClick}
      />
    </>
  );
};

export default CartButton;
